import { useParams } from 'react-router-dom'
import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { CheckValidateProject } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/utilities/checkValidateProject'
import { ValidateProject } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/utilities/validateProject'
import { toast } from 'react-toastify'
import { useAccount } from 'wagmi'
import Web3 from 'web3'

import { axiosInstance } from 'src/util/axios.js'
import { useAuthState } from 'src/contexts/authContext'
import useBlockchainAdapter from 'src/hooks/useBlockchainAdapter'
import { formatNumber3Decimals } from 'src/util/formats'
import { networkType, networksData, chainsDataType } from 'src/util/chainsData'

const ethereum = window.ethereum
export interface IProject {
  id: number
  txHash: string
  status: string
  projectType: string
  abi: any[]
  scAddress?: string
  hasSubscription?: boolean
  data: {
    tokenName: string
    tokenTicker: string
    tokenSymbol: string
    nftSymbol: string
    nftCollectionName: string
    contractName: string
    chain: string
    network: string
    networkProvider: string
    detailedTokenTicker: string
    scAddress?: string
    initialSupply?: number
    numberOfDecimals?: number
    decimals?: number
    vestedTokenAddress?: string
    vestedTokenAddressDetails?: {
      decimals: string
      name: string
      supply: string
      symbol: string
    }
    stakedTokenAddress?: string
    stakedTokenAddressDetails?: {
      decimals: string
      name: string
      supply: string
      symbol: string
    }
  }
  properties: {
    canBurn: Boolean
    canMint: Boolean
    canAddSpecialRoles?: Boolean
    canChangeOwner?: Boolean
    canFreeze?: Boolean
    canPause?: Boolean
    canUpgrade?: Boolean
    canWipe?: Boolean
  }
}

const useProjectsApi = () => {
  const user = useAuthState().user
  let { projectId } = useParams()
  const adapter = useBlockchainAdapter()
  const walletConnectAccount = useAccount()

  const zoid = new ZoidCore({
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  })

  const getTokenDetails = async ({
    scAddress,
    address,
    tokenTicker,
    chain,
    network,
    networkProvider,
  }: {
    scAddress: string
    address: string
    tokenTicker: string
    chain: string
    network: string
    networkProvider: string
  }) => {
    try {
      const chainId = network === 'mainnet' ? 1 : 11155111
      const response = await axiosInstance.post('/blockchain-helpers/getTokenDetails', {
        chain,
        environment: network,
        networkProvider,
        ...(chain === 'ethereum' ? { chainId } : {}),
        ...(chain === 'multiversx' ? { tokenTicker, address } : { scAddress }),
      })

      return response.data.data
    } catch (err) {
      console.error('Get project error', err)
    }
  }

  const getProject = async () => {
    try {
      const response = await axiosInstance.post('/blockchain-helpers/getProjectById', {
        id: parseInt(projectId || '', 10),
      })

      let tokenDetails
      if (response.data.data.status === 'verified') {
        const projectDetails = response.data.data

        tokenDetails = await getTokenDetails({
          scAddress:
            projectDetails?.data?.vestedTokenAddress ||
            projectDetails?.data?.stakedTokenAddress ||
            projectDetails.scAddress,
          address: projectDetails.data.address,
          tokenTicker: projectDetails.data.detailedTokenTicker,
          chain: projectDetails.data.chain,
          network: projectDetails.data.network,
          networkProvider: projectDetails.data.networkProvider,
        })
      }

      return {
        ...response.data.data,
        properties: {
          canUpgrade: tokenDetails?.canUpgrade || false,
          canMint: tokenDetails?.canMint || false,
          canBurn: tokenDetails?.canBurn || false,
          canChangeOwner: tokenDetails?.canChangeOwner || false,
          canAddSpecialRoles: tokenDetails?.canAddSpecialRoles || false,
          canPause: tokenDetails?.canPause || false,
          canFreeze: tokenDetails?.canFreeze || false,
          canWipe: tokenDetails?.canWipe || false,
        },
      }
    } catch (err) {
      console.error('Get project error', err)
    }
  }

  const deleteProject = async () => {
    try {
      const res = await axiosInstance.post('/blockchain-helpers/external/deleteProject', {
        id: parseInt(projectId || ''),
      })
      return res.data.data
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const subscribeProject = async () => {
    try {
      await axiosInstance.post('/blockchain-helpers/external/createExplorerToken', {
        projectId: Number(projectId),
      })

      await getProject()
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const unsubscribeProject = async () => {
    try {
      await axiosInstance.post('/blockchain-helpers/external/unsubscribeProject', {
        projectId: Number(projectId),
      })

      await getProject()
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const getProjectEvents = async ({ limit, offset }: { limit: number; offset: number }) => {
    try {
      const response = await axiosInstance.post(
        '/blockchain-helpers/external/getExplorerERC20Transactions',
        {
          projectId: Number(projectId),
          limit,
          offset,
        },
      )

      return response?.data?.data
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
      return []
    }
  }

  const checkValidateProject = async (project: IProject) => {
    try {
      let txReceipt
      if (project?.data?.chain === 'multiversx') {
        txReceipt = null
      } else {
        txReceipt = await zoid.utils.getTxReceipt({
          txHash: project?.txHash,
          networkProvider: project?.data.networkProvider,
        })
      }

      const validatePayload: CheckValidateProject = {
        id: project.id,
        smartContractAddress: txReceipt ? txReceipt.data.data.contractAddress : '',
      }

      await zoid.utils.checkValidate(validatePayload)
      await getProject()
    } catch (err) {
      if (err instanceof Error) {
        toast.error(
          'Something went wrong. It may take a few more minutes until it gets validated. Please try again.',
          {
            progressClassName: 'simpleBar',
          },
        )
      }
      throw err
    }
  }

  const validateProject = async (project: IProject) => {
    try {
      let txReceipt
      if (project?.data?.chain === 'multiversx') {
        txReceipt = null
      } else {
        txReceipt = await zoid.utils.getTxReceipt({
          txHash: project?.txHash,
          networkProvider: project?.data.networkProvider,
        })
      }

      const validatePayload: ValidateProject = {
        id: project.id,
        contractAddress: txReceipt ? txReceipt.data.data.contractAddress : '',
      }

      await zoid.utils.validateProject(validatePayload)
    } catch (err) {
      console.error('Project validate error', err)
      throw err
    }
  }

  const executeAction = async ({ data, project }: { data: string; project: IProject }) => {
    const params = {
      from: walletConnectAccount?.address || adapter.metamask?.connector?.selectedAddress,
      to: project?.scAddress,
      data,
    }
    try {
      await ethereum?.request({
        method: 'eth_sendTransaction',
        params: [params],
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const getContractDetails = async ({
    action,
    inputsObject,
    inputsKeys,
    project,
  }: {
    action: string
    inputsObject: any
    inputsKeys: string[]
    project: IProject
  }) => {
    try {
      const web3 = new Web3(
        new Web3.providers.HttpProvider(
          networksData[project?.data?.chain as keyof chainsDataType][
            project?.data?.network as networkType
          ].rpc[0],
        ),
      )
      const erc20Contract = new web3.eth.Contract(project.abi, project?.scAddress)

      const param1 = inputsObject ? inputsObject[inputsKeys[0]] : undefined
      const param2 = inputsObject ? inputsObject[inputsKeys[1]] : undefined

      //@ts-ignore
      const details = await erc20Contract.methods[action]?.(param1, param2)?.call()

      const decimals =
        project?.data?.numberOfDecimals ||
        parseInt(project?.data?.stakedTokenAddressDetails?.decimals || '0') ||
        parseInt(project?.data?.vestedTokenAddressDetails?.decimals || '0')

      switch (action) {
        case 'totalSupply':
        case 'balanceOf':
        case 'freezeOf':
        case 'allowance':
        case 'totalRewards':
        case 'totalStaked':
        case 'viewRewards':
          return formatNumber3Decimals(
            //@ts-ignore
            Number(details / BigInt(10 ** decimals)),
          )
        case 'decimals':
        case 'calculateReward':
        case 'rewardRateDenominator':
        case 'rewardRateNumerator':
          return Number(details)
        case 'vested':
          return {
            amount: formatNumber3Decimals(
              //@ts-ignore
              Number(details.amount / BigInt(10 ** decimals)),
            ),
            // @ts-ignore
            // expiry: Number(details.expiry) ? new Date(Number(details.expiry) * 1000) : '',
            expiry: Number(details.expiry),
            // @ts-ignore
            receiver: details.receiver,
            // @ts-ignore
            sender: details.sender,
          }
        case 'stakes':
          return {
            amount: formatNumber3Decimals(
              //@ts-ignore
              Number(details.amount / BigInt(10 ** decimals)),
            ),
            // @ts-ignore
            startTime: Number(details?.startTime),
            // @ts-ignore
            savedReward: Number(details.savedReward / BigInt(10 ** decimals)),
          }
        default:
          return details
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  return {
    getProject,
    deleteProject,
    subscribeProject,
    unsubscribeProject,
    getProjectEvents,
    checkValidateProject,
    validateProject,
    executeAction,
    getContractDetails,
  }
}

export default useProjectsApi
